import React, { useEffect, useMemo, useState } from 'react';
import styles from './DocumentChecklist.module.scss';
import { IChecklistItem } from 'src/shared/types/Documents';
import { ListBox } from 'primereact/listbox';
import classNames from 'classnames';
import minusIcon from 'src/assets/icons/minus.svg';
import plusIcon from 'src/assets/icons/plus.svg';
import outerCircleIcon from 'src/assets/icons/outer-circle.svg';
import navigationCheckIcon from 'src/assets/icons/navigation-check.svg';
import ChecklistProgressBar from '@ds/elements/ChecklistProgressBar/ChecklistProgressBar';
import { isChecklistItemComplete } from 'src/utils/services/documents';

type MemberType = 'BENEFICIARY' | 'DEPENDENT';

interface IMember {
  uiKey: string;
  id: number;
  name: string;
  type: MemberType;
  relationship?: string;
}

interface Props {
  items: IChecklistItem[];
  currentItem: IChecklistItem;
  onChecklistItemChange: (item: IChecklistItem) => void;
}

const DocumentChecklist: React.FC<Props> = ({ items, currentItem, onChecklistItemChange }) => {
  const [currentMemberId, setCurrentMemberId] = useState<string>('');

  const convertChecklistItemToMember = (checklistItem: IChecklistItem): IMember => {
    const type: MemberType = checklistItem.beneficiaryId ? 'BENEFICIARY' : 'DEPENDENT';

    return {
      uiKey: `${type}-${checklistItem.beneficiaryId ?? checklistItem.dependentId}`,
      id: checklistItem.beneficiaryId ?? checklistItem.dependentId ?? 0,
      name: checklistItem.beneficiary?.fullName || checklistItem.dependent?.dependentFullName || '',
      type,
      relationship: checklistItem.dependent?.relationship || '',
    };
  };

  const uniqueMembers = useMemo(() => {
    const mapped = items.map(convertChecklistItemToMember);

    const uniqueMap = new Map<string, IMember>();
    for (const m of mapped) {
      if (!uniqueMap.has(m.uiKey)) {
        uniqueMap.set(m.uiKey, m);
      }
    }

    const deduped = Array.from(uniqueMap.values());

    deduped.sort((a, b) => {
      const relScore = (rel?: string) => (rel === 'SPOUSE' ? 1 : rel ? 2 : 0);
      const scoreDiff = relScore(a.relationship) - relScore(b.relationship);

      if (scoreDiff !== 0) return scoreDiff;
      if (a.name !== b.name) return a.name.localeCompare(b.name);
      if (a.type !== b.type) return a.type.localeCompare(b.type);
      return a.id - b.id;
    });

    setCurrentMemberId(deduped[0]?.uiKey);
    return deduped;
  }, [items]);

  useEffect(() => {
    if (currentMemberId === '' && uniqueMembers.length > 0) {
      setCurrentMemberId(uniqueMembers[0].uiKey);
    }
  }, [uniqueMembers, currentMemberId]);

  const checklistItemsOfActiveMember = useMemo(
    () => items.filter((item) => convertChecklistItemToMember(item).uiKey === currentMemberId),
    [items, currentMemberId]
  );

  const renderChecklistItem = (checklistItem: IChecklistItem) => {
    const carryForwardedItems = checklistItem.checklistItemDocuments.filter(
      (d) => d.isAutoCarryForward && !d.isAutoCarryForwardDisplayed
    );

    const isDisplayDocumentCarryForwarded = carryForwardedItems.length > 0;
    const isComplete = isChecklistItemComplete(checklistItem);
    const icon = isComplete ? navigationCheckIcon : outerCircleIcon;

    return (
      <div key={`${checklistItem.checklistDefinition?.migrationId}`} className={styles['checklist-item']}>
        <div className={styles['item-content']}>
          <img src={icon} className={styles['complete-icon']} />
          <span className={styles['droppable-label']} id={`${checklistItem.id}`}>
            {checklistItem.checklistDefinition?.name}
          </span>
        </div>

        {isDisplayDocumentCarryForwarded && (
          <span className={`document-carry-forwarded-${checklistItem.id}`}>
            <div className={styles['carry-forward-icon']} />
          </span>
        )}
      </div>
    );
  };

  const renderMember = (member: IMember) => {
    const isActive = member.uiKey === currentMemberId;

    const applicableItems = checklistItemsOfActiveMember.filter(
      (item) => item.checklistDefinition?.checklistType !== 'LEGAL_TEAM' || !item.isNotApplicable
    );

    const totalCount = applicableItems.filter((item) => item.isShowing).length;
    const completedCount = applicableItems.filter(isChecklistItemComplete).length;
    const isCompleted = completedCount === totalCount;

    if (isActive) {
      return (
        <div className={styles['checklist']} key={member.uiKey}>
          <div
            className={classNames(styles['checklist-header'], styles['checklist-active-top'])}
            onClick={() => setCurrentMemberId(member.uiKey)}
          >
            <div className={styles['checklist-preview']}>
              {uniqueMembers.length > 1 && (
                <div className={styles['icon']}>
                  <img src={minusIcon} alt="Collapse section" />
                </div>
              )}
              <p className={styles['name']}>{member.name}</p>
              <p className={classNames({ [styles['checklist-complete']]: isCompleted })}>
                {`${completedCount}/${totalCount}`}
              </p>
            </div>
            <ChecklistProgressBar total={totalCount} progress={completedCount} />
          </div>

          <div className={styles['checklist-active-bottom']}>
            <div className={styles['drop-zone-container']}>
              <ListBox
                value={currentItem}
                itemTemplate={renderChecklistItem}
                options={checklistItemsOfActiveMember}
                onChange={(evt) => {
                  if (evt.value) {
                    onChecklistItemChange(evt.value);
                  }
                }}
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={member.uiKey}>
        <div className={styles['checklist-header']} onClick={() => setCurrentMemberId(member.uiKey)}>
          <div className={styles['checklist-preview']}>
            <div className={styles['icon']}>
              <img src={plusIcon} alt="Expand section" />
            </div>
            <p className={styles['name']}>{member.name}</p>
          </div>
        </div>
      </div>
    );
  };

  return <div className={styles['case-docs-checklist']}>{uniqueMembers.map(renderMember)}</div>;
};

export default DocumentChecklist;
